import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { db } from '@/data/supabase';

const REQUIRED = [
  { kind: 'before', label: 'Before — Full pool view' },
  { kind: 'after',  label: 'After — Full pool view' },
];
const OPTIONAL = [
  { kind: 'equipment', label: 'Equipment area' },
  { kind: 'issue',     label: 'Issue (if any)' },
];

function PhotoRow({ kind, label, photo, busy, onCapture, required }) {
  const fileRef = useRef(null);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 14px',
      borderBottom: '1px solid var(--b1)',
    }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onCapture(f);
          e.target.value = '';
        }}
      />
      <div
        onClick={() => !busy && fileRef.current?.click()}
        style={{
          width: 56,
          height: 56,
          borderRadius: 8,
          background: photo ? 'transparent' : 'var(--s3)',
          border: photo ? '1px solid var(--teal)' : '2px dashed var(--b2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          flexShrink: 0,
          cursor: busy ? 'wait' : 'pointer',
          overflow: 'hidden',
        }}
      >
        {photo ? (
          <img src={photo.url} alt={kind} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : '📷'}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
          {label} {required && !photo && <span style={{ color: 'var(--rose)', fontSize: 11 }}>· required</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
          {photo ? `Captured ${new Date(photo.taken_at || photo.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Tap to capture'}
        </div>
      </div>
      <div style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: photo ? '2px solid var(--teal)' : '2px solid var(--b2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 13,
        color: 'var(--teal)',
      }}>
        {photo ? '✓' : ''}
      </div>
    </div>
  );
}

export default function Photos() {
  const { activeStop, custById, uploadPhoto, completeStop, setMobileScreen, cloudMode, hasPlugin, showToast } = useApp();
  const c = activeStop ? custById(activeStop.custId) : null;
  const requirePhotos = hasPlugin('photos'); // core plugin; default on. Use to gate completion.

  const [photos, setPhotos]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);

  useEffect(() => {
    if (!activeStop) return;
    // In local mode, photos are kept only in component state (no DB).
    if (!cloudMode) { setPhotos([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await db.photos.listForStop(activeStop.id);
      if (!cancelled) { setPhotos(res?.data || []); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [activeStop?.id, cloudMode]);

  const byKind = (k) => photos.find(p => p.kind === k);
  const handleCapture = async (kind, file) => {
    setUploadingKind(kind);
    // Local-mode fallback: build a data URL so the user can verify the flow end-to-end.
    if (!cloudMode) {
      try {
        const url = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload  = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const localPhoto = {
          id:         `local-${kind}-${Date.now()}`,
          kind,
          url,
          taken_at:   new Date().toISOString(),
          stop_id:    activeStop?.id,
          customer_id: c?.id,
        };
        setPhotos(prev => [...prev.filter(p => p.kind !== kind), localPhoto]);
        showToast?.('Photo captured (local) ✓');
      } catch (err) {
        showToast?.('Photo capture failed', false);
      } finally {
        setUploadingKind(null);
      }
      return;
    }
    const res = await uploadPhoto({
      file,
      stopId: activeStop?.id,
      customerId: c?.id,
      kind,
      tech: activeStop?.tech,
    });
    setUploadingKind(null);
    if (res.ok && res.photo) setPhotos(prev => [...prev.filter(p => p.kind !== kind), res.photo]);
  };

  const missingRequired = REQUIRED.filter(r => !byKind(r.kind));
  const canComplete = missingRequired.length === 0;

  const handleComplete = () => {
    if (!canComplete) return;
    completeStop(activeStop.id);
    setMobileScreen('day');
  };

  if (!activeStop || !c) {
    return (
      <div className="mobile-page">
        <div style={{ textAlign: 'center', color: 'var(--t3)', padding: 40 }}>
          No active stop selected
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <div className="mobile-hero" style={{ margin: '-14px -16px 0', padding: '14px 16px 16px' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--t1)', letterSpacing: '-.3px' }}>
          Photo Proof
        </div>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 3 }}>{c.name}</div>
      </div>

      <div className="m-card">
        <div style={{
          padding: '9px 14px',
          borderBottom: '1px solid var(--b1)',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--t3)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span>Required Shots</span>
          {loading && <span style={{ color: 'var(--t3)' }}>Loading…</span>}
        </div>
        {REQUIRED.map(r => (
          <PhotoRow
            key={r.kind}
            kind={r.kind}
            label={r.label}
            required
            photo={byKind(r.kind)}
            busy={uploadingKind === r.kind}
            onCapture={(f) => handleCapture(r.kind, f)}
          />
        ))}
      </div>

      <div className="m-card">
        <div style={{
          padding: '9px 14px',
          borderBottom: '1px solid var(--b1)',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--t3)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
        }}>
          Optional
        </div>
        {OPTIONAL.map(r => (
          <PhotoRow
            key={r.kind}
            kind={r.kind}
            label={r.label}
            photo={byKind(r.kind)}
            busy={uploadingKind === r.kind}
            onCapture={(f) => handleCapture(r.kind, f)}
          />
        ))}
      </div>

      <button
        className="m-action primary"
        style={{ width: '100%', opacity: canComplete ? 1 : 0.5 }}
        disabled={!canComplete}
        onClick={handleComplete}
        title={canComplete ? '' : `Capture ${missingRequired.map(r => r.label.split(' — ')[0]).join(' + ')} first`}
      >
        <div className="m-action-icon">✅</div>
        <div className="m-action-label" style={{ fontSize: 14 }}>
          {canComplete ? 'Mark Stop Complete' : `Missing: ${missingRequired.map(r => r.label.split(' — ')[0]).join(' + ')}`}
        </div>
      </button>
    </div>
  );
}
